import type { OrderedFiles } from '@/lib/ai/ordered-files-service/order-files-prompts';

export const ROOT_FILES_GROUP_NAME = '(root files)';

export type OrderedFilesGroupedItem<TFile> = {
  groupName: string;
  groupOrder: number;
  groupNameReason?: string;
  files: TFile[];
};

type FileLike = {
  filename: string;
};

export function getFallbackGroupName(filename: string): string {
  const slashIndex = filename.indexOf('/');
  if (slashIndex === -1) {
    return ROOT_FILES_GROUP_NAME;
  }

  return filename.slice(0, slashIndex);
}

export function compareFallbackGroupNames(left: string, right: string): number {
  if (left === ROOT_FILES_GROUP_NAME && right !== ROOT_FILES_GROUP_NAME) {
    return -1;
  }

  if (right === ROOT_FILES_GROUP_NAME && left !== ROOT_FILES_GROUP_NAME) {
    return 1;
  }

  return left.localeCompare(right);
}

export function groupFilesByOrderedFiles<TFile extends FileLike>(
  files: TFile[],
  orderedFiles: OrderedFiles[],
): OrderedFilesGroupedItem<TFile>[] {
  const orderedFilesByFilename = new Map(orderedFiles.map((file) => [file.filename, file]));
  const originalIndexByFilename = new Map(files.map((file, index) => [file.filename, index]));
  const groups = new Map<string, OrderedFilesGroupedItem<TFile>>();

  files.forEach((file) => {
    const orderedFile = orderedFilesByFilename.get(file.filename);
    const groupName = orderedFile?.groupName ?? getFallbackGroupName(file.filename);
    const groupOrder = orderedFile?.groupOrder ?? Number.MAX_SAFE_INTEGER;
    const existing = groups.get(groupName);

    if (!existing) {
      groups.set(groupName, {
        groupName,
        groupOrder,
        groupNameReason: orderedFile?.groupNameReason,
        files: [file],
      });
      return;
    }

    existing.groupOrder = Math.min(existing.groupOrder, groupOrder);
    if (!existing.groupNameReason && orderedFile?.groupNameReason) {
      existing.groupNameReason = orderedFile.groupNameReason;
    }
    existing.files.push(file);
  });

  return Array.from(groups.values())
    .map((group) => ({
      ...group,
      files: [...group.files].sort((a, b) => {
        const aOrdered = orderedFilesByFilename.get(a.filename);
        const bOrdered = orderedFilesByFilename.get(b.filename);
        const aOrderNumber =
          typeof aOrdered?.orderNumber === 'number'
            ? aOrdered.orderNumber
            : Number.MAX_SAFE_INTEGER;
        const bOrderNumber =
          typeof bOrdered?.orderNumber === 'number'
            ? bOrdered.orderNumber
            : Number.MAX_SAFE_INTEGER;
        if (aOrderNumber !== bOrderNumber) {
          return aOrderNumber - bOrderNumber;
        }

        const aIndex = originalIndexByFilename.get(a.filename) ?? Number.MAX_SAFE_INTEGER;
        const bIndex = originalIndexByFilename.get(b.filename) ?? Number.MAX_SAFE_INTEGER;
        return aIndex - bIndex;
      }),
    }))
    .sort((a, b) => {
      if (a.groupOrder !== b.groupOrder) {
        return a.groupOrder - b.groupOrder;
      }
      return compareFallbackGroupNames(a.groupName, b.groupName);
    });
}
